import type { SmartCreationBrowserDraft } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { SelectedProductsPreview } from "@/components/smart-creation/SelectedProductsPreview";

export interface StrategyContextSummaryProps {
  plan: SmartCreationBrowserDraft;
  currencyCode?: string;
}

export function StrategyContextSummary({ plan, currencyCode }: StrategyContextSummaryProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const { draft } = plan;
  const groups = new Set(draft.products.map((product) => product.variationGroupKey)).size;
  const objectiveKeys = {
    drive_growth: "driveGrowth",
    optimize_roas: "optimizeRoas",
    launch_product: "launchProduct",
    promotion_sales_boost: "promotion",
  };
  const objectiveKey = draft.objective === null ? null : objectiveKeys[draft.objective];
  const percent = new Intl.NumberFormat(i18n.resolvedLanguage, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const resolvedCurrency =
    currencyCode ?? plan.previewDraft?.currencyCode ?? plan.reviewWorkspace?.summary.currencyCode;
  const currency = new Intl.NumberFormat(i18n.resolvedLanguage, {
    ...(resolvedCurrency ? { style: "currency", currency: resolvedCurrency } : {}),
    maximumFractionDigits: 2,
  });
  const personality =
    draft.aiPersonality ??
    (draft.riskPreference === "conservative" ? 1 : draft.riskPreference === "aggressive" ? 5 : 3);

  return (
    <section
      data-strategy-context
      className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg bg-subtle px-4 py-3"
    >
      <SummaryValue label={t("strategy.context.parentAsins")} value={String(groups)} />
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t("strategy.context.skus")}</span>
        <SelectedProductsPreview products={draft.products} shopId={plan.shopId} />
      </div>
      <SummaryValue
        label={t("strategy.context.objective")}
        value={objectiveKey === null ? "—" : t(`objectives.${objectiveKey}`)}
      />
      <SummaryValue
        label={t("strategy.context.targetAcos")}
        value={draft.targetAcos === null ? "—" : percent.format(draft.targetAcos)}
      />
      <SummaryValue
        label={t("strategy.context.budget")}
        value={draft.totalDailyBudget === null ? "—" : currency.format(draft.totalDailyBudget)}
      />
      <SummaryValue label={t("strategy.context.personality")} value={String(personality)} />
    </section>
  );
}

function SummaryValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex min-h-8 items-center text-[13px] font-semibold text-ink">{value}</span>
    </div>
  );
}
